import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import cn from "classnames";
import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    TextArea,
} from "@jhool-io/fe-components";
import { useDebounce } from "../../../../hooks/helpers";
import useToast from "../../../../hooks/useToast";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { useFetchUsersList } from "../../../user-management/hooks/user-management.queries";

import ListState from "../../../../components/ListState/ListState";
import {
    formatDate,
    removeEnumUnderscore,
    truncateString,
    userInitials,
} from "../../../../utils/helpers";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import {
    useAddNoteMessaging,
    useDeleteMessage,
} from "../../hooks/notes.mutations";
import { IAddNoteMessage, IDeleteNoteMessage } from "../../types/notes.types";
import { useFetchMessages } from "../../hooks/notes.queries";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import { UserPermisions } from "../../../../utils/types/user";

type AppComboBoxOptions = {
    value: string;
    label: string;
};

function Messages() {
    // Toast for success and error states
    const { toast } = useToast();

    // Query client
    const queryClient = useQueryClient();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const textarea = textareaRef.current;
    const [userSearchString, setUserSearchString] = React.useState("");
    const [showUsersListPopover, setShowUsersListPopover] =
        React.useState(false);
    const [messageType, setMessageType] = useState<"provider" | "public">(
        "provider"
    );

    const [messageSearchString, setMessageSearchString] = useState("");
    const debouncedUserSearchValue = useDebounce(userSearchString, 500);
    const debouncedMessageSeachString = useDebounce(
        (messageSearchString.length > 3 && messageSearchString) || "",
        500
    );
    const [message, setMessage] = useState("");
    const [showPrompt, setShowPrompt] = useState(false);
    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;
    const [selectedMessageId, setSelectedMessageId] = useState("");

    // Hook to get logged in user
    const loggedInUser = useFetchUserDetails();

    //  Hook to add note message
    const addMessage = useAddNoteMessaging(noteId);

    // Hook to fetch session note
    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    // Hook to fetch messages
    const {
        data: noteMessages,
        isLoading,
        isSuccess,
        error,
    } = useFetchMessages(noteId, debouncedMessageSeachString);

    // hook to delete messages
    const deleteNoteMessage = useDeleteMessage(noteId);

    // Fetch internal users
    const usersList = useFetchUsersList({
        search_string: userSearchString
            ? debouncedUserSearchValue?.toLowerCase()
            : `${data?.data.provider.first_name} ${data?.data.provider.last_name}`,
        page: 0,
        limit: 20,
    });

    const [selectedRecipientOption, setSelectedRecipientOption] =
        React.useState<AppComboBoxOptions | null>({
            value: "",
            label: "",
        });
    // check if add message is mutating
    const addMessageIsMutating = useIsMutating(["add_message", noteId]);

    const canSendNoteMessage = useDoesUserHavePermission([
        UserPermisions.NOTE_MESSAGING,
    ]);

    // function to create a note message
    const handleAddNoteMessage = () => {
        let dataToSend: IAddNoteMessage;
        if (!message) {
            return;
        }
        if (messageType === "public") {
            dataToSend = {
                is_public: true,
                message,
            };
        } else {
            dataToSend = {
                is_public: false,
                recipient_user_id: selectedRecipientOption?.label
                    ? selectedRecipientOption?.value
                    : usersList?.data?.data[0]?.user_id,
                message,
            };
        }
        addMessage.mutate(dataToSend, {
            onSuccess: (res) => {
                toast({
                    mode: "success",
                    message: res.message || "Message sent successfully",
                });
                if (textarea) {
                    textarea.style.height = "4.5rem";
                    textarea.style.overflowY = "hidden";
                }
                setMessage("");
                queryClient.invalidateQueries({
                    queryKey: [noteId, "get-messages"],
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not send message at this time",
                });
            },
        });
    };

    // function to delete a note message
    const handleDeleteNoteMessage = () => {
        const dataToSend: IDeleteNoteMessage = {
            message_id: selectedMessageId,
        };
        deleteNoteMessage.mutate(dataToSend, {
            onSuccess: (res) => {
                toast({
                    mode: "success",
                    message: res.message || "Message deleted successfully",
                });
                setShowPrompt(false);
                queryClient.invalidateQueries({
                    queryKey: [noteId, "get-messages"],
                });
            },
            onError: (err) => {
                setShowPrompt(false);
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete message at this time",
                });
            },
        });
    };

    const escapeRegExp = (value: string) =>
        value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const getHighlightedContent = (text: string): React.ReactNode => {
        if (!messageSearchString) {
            return text;
        }

        const escapedSearch = escapeRegExp(messageSearchString);
        const regex = new RegExp(escapedSearch, "gi");
        const matches = text.match(regex);

        if (!matches) {
            return text;
        }

        const parts = text.split(regex);
        const remainingMatches = [...matches];

        return parts.reduce<React.ReactNode[]>((acc, part) => {
            if (part) {
                acc.push(part);
            }

            const match = remainingMatches.shift();

            if (match) {
                acc.push(
                    <span
                        key={`${match}-${match.length}-${text.length}-${acc.length}`}
                        className="bg-primary font-bold text-white rounded-r4"
                    >
                        {match}
                    </span>
                );
            }

            return acc;
        }, []);
    };

    const autoExpandTextarea = () => {
        const maxHeight = 100;
        if (textarea) {
            textarea.style.height = "4.5rem"; // Reset the height to recalculate
            const { scrollHeight } = textarea;
            // Adjust the textarea's height based on scrollHeight
            if (scrollHeight > maxHeight) {
                textarea.style.height = `${maxHeight}px`;
                textarea.style.overflowY = "scroll";
            } else {
                textarea.style.height = "4.5rem";
                textarea.style.overflowY = "hidden";
            }
        }
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        const { value } = event.target;
        setMessage(value);
        autoExpandTextarea();
    };

    const selectedValue =
        (userSearchString && selectedRecipientOption?.label) ||
        `${usersList?.data?.data?.[0]?.first_name} ${usersList?.data?.data?.[0]?.last_name}` ||
        "";

    return (
        <div className="h-[80vh] flex overflow-hidden">
            <AlertDialog open={showPrompt}>
                <AlertDialogContent
                    title="Delete message"
                    description="Please note that this action cannot be undone, and the message will be permanently removed from this note"
                    onCancelClick={() => setShowPrompt(false)}
                    saveText="Delete message"
                    isDeleting
                    onSaveClick={handleDeleteNoteMessage}
                    isCancelBtnDisabled={deleteNoteMessage.isLoading}
                    isSaveBtnDisabled={deleteNoteMessage.isLoading}
                />
            </AlertDialog>
            <Card classNames="flex flex-col flex-grow p-0">
                <div className="[border-bottom:1px_solid_var(--color-stroke)] py-[--padding-card-y] px-[--padding-card-x] flex items-center justify-between">
                    <div className="flex items-center gap-x-[--spacing-items]">
                        <div className="size-8 rounded-full [border:1.5px_solid_var(--border-theme-switch)] flex items-center justify-center text-caption text-text font-semibold">
                            {userInitials(
                                removeEnumUnderscore(
                                    loggedInUser?.data?.first_name || ""
                                ),
                                removeEnumUnderscore(
                                    loggedInUser?.data?.last_name || ""
                                )
                            )}
                        </div>
                        <span className="font-semibold">Chats</span>
                    </div>
                    <div>
                        <SearchInput
                            placeholder="Search by sender or by keyword"
                            value={messageSearchString}
                            containerClass="w-[300px]"
                            onChange={(e) =>
                                setMessageSearchString(e.target.value)
                            }
                        />
                    </div>
                </div>
                {error && error.response?.status !== 404 && (
                    <ListState
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display messages at this time please try again later`
                        }
                        stateHelperText="Try reloading this page to solve this issue"
                        isError
                        context="general"
                    />
                )}
                {isLoading && <Loader wrapperClassName="h-full" />}
                <div>
                    {!isLoading &&
                        noteMessages &&
                        isSuccess &&
                        noteMessages.data.length === 0 && (
                            <ListState
                                emptyMessage="No message history yet"
                                stateHelperText={
                                    canSendNoteMessage
                                        ? "You can send a message about this note to the provider or anyone in the practice"
                                        : "Messages about this note will appear here"
                                }
                                isError={false}
                            />
                        )}
                </div>
                <div className="flex-grow overflow-auto py-[--padding-card-y-lg]">
                    {!isLoading &&
                        noteMessages &&
                        noteMessages.data.length > 0 &&
                        noteMessages?.data.map((msg) => {
                            const firstName = msg.sender_first_name;
                            const lastName = msg.sender_last_name;
                            const highlightedUser = getHighlightedContent(
                                `${firstName} ${lastName}`
                            );
                            const highlightedText = getHighlightedContent(
                                msg.message
                            );
                            return (
                                <div key={msg.message_id}>
                                    <div className="flex text-base px-[--padding-card-x] py-[--padding-card-y-lg] h-auto items-center">
                                        <div className="size-8 rounded-full [border:1.5px_solid_var(--border-theme-switch)] flex items-center justify-center text-caption text-text font-semibold">
                                            {userInitials(
                                                removeEnumUnderscore(firstName),
                                                removeEnumUnderscore(lastName)
                                            )}
                                        </div>
                                        <div className="flex items-center">
                                            <div className="pl-12 text-textSec text-caption font-semibold">
                                                {highlightedUser}
                                            </div>

                                            <div className="w-[3px] h-[3px] bg-textSec rounded-[50%] ml-1" />
                                            <div className="pl-[6px] text-textSec text-caption font-semibold">
                                                {`${formatDate(
                                                    msg.sent_time as string,
                                                    false,
                                                    "MMM dd"
                                                )},`}
                                            </div>
                                            <div className="pl-[6px] text-textSec text-caption font-semibold">
                                                {formatDate(
                                                    msg.sent_time as string,
                                                    false,
                                                    "h:mm a"
                                                )}
                                            </div>
                                        </div>
                                        {msg.is_public === true && (
                                            <div className="flex items-center">
                                                <div className="w-[3px] h-[3px] bg-textSec rounded-[50%] ml-1" />
                                                <div className="pl-8 text-textSec text-caption font-semibold">
                                                    Public
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="h-auto md:w-[500px] ml-[calc(var(--padding-card-x)+42px)] w-full py-[--padding-card-y] px-[--padding-card-x] mb- rounded-md bg-[--card-bg-gray]">
                                        <div className="w-full text-captipn font-normal ">
                                            {highlightedText}
                                        </div>
                                        <div className="flex justify-end mt-[--padding-card-y]">
                                            {canSendNoteMessage &&
                                                `${loggedInUser.data?.first_name} ${loggedInUser.data?.last_name}` ===
                                                    `${msg.sender_first_name} ${msg.sender_last_name}` && (
                                                    <Dropdown>
                                                        <DropdownTrigger
                                                            asChild
                                                        >
                                                            <MoreButton
                                                                onPointerDown={() => {
                                                                    setSelectedMessageId(
                                                                        msg.message_id
                                                                    );
                                                                }}
                                                            />
                                                        </DropdownTrigger>
                                                        <DropdownContent
                                                            align="end"
                                                            width="auto"
                                                        >
                                                            <DropdownItem
                                                                onClick={() => {
                                                                    setShowPrompt(
                                                                        !showPrompt
                                                                    );
                                                                }}
                                                                className="text-danger"
                                                            >
                                                                Delete Message
                                                            </DropdownItem>
                                                        </DropdownContent>
                                                    </Dropdown>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
                {canSendNoteMessage && (
                    <div className="flex flex-col gap-y-[--spacing-items] pb-[--padding-card-y] [border-top:1px_solid_var(--color-stroke)] flex-none">
                        <div className="flex gap-x-2 px-[--padding-card-x] pt-12">
                            <Popover
                                open={showUsersListPopover}
                                onOpenChange={setShowUsersListPopover}
                            >
                                <PopoverTrigger
                                    asChild
                                    onClick={() =>
                                        setShowUsersListPopover(true)
                                    }
                                    disabled={usersList.isLoading}
                                >
                                    <FilterButton
                                        className={cn("", {
                                            capitalize: selectedValue,
                                        })}
                                        onClick={() => {
                                            setMessageType("provider");
                                        }}
                                    >
                                        <span className="flex items-center gap-x-1">
                                            <span className="flex items-center justify-center size-4 rounded-full [border:1px_solid_var(--color-stroke)]">
                                                <span
                                                    className={cn(
                                                        "size-2 bg-textLink rounded-full hidden",
                                                        {
                                                            "!block":
                                                                messageType ===
                                                                "provider",
                                                        }
                                                    )}
                                                />
                                            </span>
                                            Send to:{" "}
                                            {truncateString(
                                                selectedValue || "",
                                                18
                                            )}
                                        </span>
                                    </FilterButton>
                                </PopoverTrigger>

                                <PopoverContent
                                    className="w-[400px] max-w-full max-h-80 overflow-y-auto"
                                    align="start"
                                >
                                    <SearchInput
                                        placeholder="Search by provider name"
                                        onChange={(e) =>
                                            setUserSearchString(e.target.value)
                                        }
                                    />

                                    <div className="py-2">
                                        {usersList.isLoading && (
                                            <Loader wrapperClassName="h-[150px]" />
                                        )}

                                        {usersList.error && (
                                            <ListState
                                                isError
                                                context="general"
                                                cardClass="h-[150px]"
                                                stateHelperText="Please try again later"
                                                errorMsg={
                                                    usersList.error?.response
                                                        ?.data?.message ||
                                                    "Error loading providers"
                                                }
                                            />
                                        )}

                                        {usersList.data?.data &&
                                            usersList.isSuccess && (
                                                <div>
                                                    {usersList.data?.data &&
                                                        usersList.data.data
                                                            .length > 0 &&
                                                        usersList?.data?.data.map(
                                                            (user) => (
                                                                <Button
                                                                    key={
                                                                        user.user_id
                                                                    }
                                                                    size="auto"
                                                                    variant="can-hover"
                                                                    className="font-medium capitalize w-full justify-start shrink-0  my-1"
                                                                    onClick={() => {
                                                                        setSelectedRecipientOption(
                                                                            {
                                                                                value: user.user_id,
                                                                                label: `${user.first_name} ${user.last_name}`,
                                                                            }
                                                                        );
                                                                        setShowUsersListPopover(
                                                                            false
                                                                        );
                                                                    }}
                                                                >
                                                                    {`${user.first_name} ${user.last_name}`}
                                                                </Button>
                                                            )
                                                        )}
                                                </div>
                                            )}
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <Button
                                variant="normal"
                                size="auto"
                                className="bg-[--card-bg] text-text shadow-[--card-shadow] filter:[--card-blur] border ![border:1px_solid_var(--color-stroke)] h-7 px-3 hover:brightness-90 disabled:hover:bg-transparent"
                                onClick={() => {
                                    setMessageType("public");
                                }}
                            >
                                <span className="flex items-center gap-x-1">
                                    <span className="flex items-center justify-center size-4 rounded-full [border:1px_solid_var(--color-stroke)]">
                                        <span
                                            className={cn(
                                                "size-2 bg-textLink rounded-full hidden",
                                                {
                                                    "!block":
                                                        messageType ===
                                                        "public",
                                                }
                                            )}
                                        />
                                    </span>
                                    Send to: Public
                                </span>
                            </Button>
                        </div>
                        <div className="flex gap-x-[--spacing-items] px-[--padding-card-x] w-full justify-between">
                            <div className="flex-1">
                                <TextArea
                                    placeholder="Enter your message"
                                    onChange={handleInputChange}
                                    style={{
                                        height: "50px",
                                        overflow: "hidden",
                                    }}
                                    className="grow-1"
                                    value={message}
                                    ref={textareaRef}
                                    id="text_area"
                                />
                            </div>
                            <Button
                                iconRight={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="size-3.5 shrink-0"
                                    >
                                        <line x1="22" y1="2" x2="11" y2="13" />
                                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                    </svg>
                                }
                                disabled={addMessageIsMutating > 0}
                                onClick={handleAddNoteMessage}
                            >
                                Send
                            </Button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}

export default Messages;
